import { motion } from 'framer-motion';
import { Palette, Check, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQRStore, colorPresets, GradientType } from '@/stores/qrStore';
import { cn } from '@/lib/utils';

export function ColorsTab() {
  const { currentQR, setDesignSettings, setGradientSettings, applyColorPreset } = useQRStore();
  const { designSettings } = currentQR;

  return (
    <div className="space-y-6">
      {/* Color Presets */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Color Presets</Label>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {colorPresets.map((preset, index) => {
            const isActive = 
              designSettings.dotsColor === preset.dotsColor && 
              designSettings.backgroundColor === preset.backgroundColor;
            
            return (
              <Tooltip key={preset.id}>
                <TooltipTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => applyColorPreset(preset)}
                    className={cn(
                      'relative h-12 rounded-lg overflow-hidden border-2 transition-all duration-200',
                      isActive 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-border/50 hover:border-primary/50'
                    )}
                    style={{
                      background: preset.gradient?.enabled
                        ? `linear-gradient(${preset.gradient.rotation}deg, ${preset.gradient.color1}, ${preset.gradient.color2})`
                        : preset.backgroundColor
                    }}
                  >
                    <div 
                      className="absolute inset-2 rounded"
                      style={{ 
                        background: `radial-gradient(circle, ${preset.dotsColor} 30%, transparent 30%)`,
                        backgroundSize: '6px 6px'
                      }}
                    />
                    {isActive && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute inset-0 flex items-center justify-center bg-primary/20"
                      >
                        <Check className="h-4 w-4 text-primary" />
                      </motion.div>
                    )}
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{preset.name}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* Main Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="dotsColor">QR Code Color</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              id="dotsColor"
              value={designSettings.dotsColor}
              onChange={(e) => {
                setDesignSettings({ 
                  dotsColor: e.target.value,
                  cornersSquareColor: e.target.value,
                  cornersDotColor: e.target.value
                });
              }}
              className="w-12 h-10 p-1 cursor-pointer"
            />
            <Input
              type="text"
              value={designSettings.dotsColor}
              onChange={(e) => {
                setDesignSettings({ 
                  dotsColor: e.target.value,
                  cornersSquareColor: e.target.value,
                  cornersDotColor: e.target.value
                });
              }}
              className="flex-1 font-mono uppercase"
              maxLength={7}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="backgroundColor">Background</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              id="backgroundColor"
              value={designSettings.backgroundColor}
              onChange={(e) => setDesignSettings({ backgroundColor: e.target.value })}
              className="w-12 h-10 p-1 cursor-pointer"
              disabled={designSettings.backgroundTransparent}
            />
            <Input
              type="text"
              value={designSettings.backgroundTransparent ? 'Transparent' : designSettings.backgroundColor}
              onChange={(e) => setDesignSettings({ backgroundColor: e.target.value })}
              className="flex-1 font-mono uppercase"
              maxLength={11}
              disabled={designSettings.backgroundTransparent}
            />
          </div>
        </div>
      </div>

      {/* Transparent Background */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="space-y-0.5">
          <Label htmlFor="transparent">Transparent Background</Label>
          <p className="text-xs text-muted-foreground">Export with no background</p>
        </div>
        <Switch
          id="transparent"
          checked={designSettings.backgroundTransparent}
          onCheckedChange={(checked) => setDesignSettings({ backgroundTransparent: checked })}
        />
      </div>

      {/* Gradient Settings */}
      <div className="space-y-4 p-4 rounded-lg border border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" />
            <Label>Background Gradient</Label>
          </div>
          <Switch
            checked={designSettings.gradient.enabled}
            onCheckedChange={(checked) => setGradientSettings({ enabled: checked })}
          />
        </div>

        {designSettings.gradient.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <RadioGroup
              value={designSettings.gradient.type}
              onValueChange={(value) => setGradientSettings({ type: value as GradientType })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="linear" id="linear" />
                <Label htmlFor="linear" className="cursor-pointer">Linear</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="radial" id="radial" />
                <Label htmlFor="radial" className="cursor-pointer">Radial</Label>
              </div>
            </RadioGroup>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color 1</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={designSettings.gradient.color1}
                    onChange={(e) => setGradientSettings({ color1: e.target.value })}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={designSettings.gradient.color1}
                    onChange={(e) => setGradientSettings({ color1: e.target.value })}
                    className="flex-1 font-mono uppercase"
                    maxLength={7}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Color 2</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={designSettings.gradient.color2}
                    onChange={(e) => setGradientSettings({ color2: e.target.value })}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={designSettings.gradient.color2}
                    onChange={(e) => setGradientSettings({ color2: e.target.value })}
                    className="flex-1 font-mono uppercase"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            {designSettings.gradient.type === 'linear' && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Angle</Label>
                  <span className="text-sm text-muted-foreground">{designSettings.gradient.rotation}°</span>
                </div>
                <Slider
                  value={[designSettings.gradient.rotation]}
                  onValueChange={([value]) => setGradientSettings({ rotation: value })}
                  min={0}
                  max={360}
                  step={15}
                />
              </div>
            )}

            {/* Gradient Preview */}
            <div 
              className="h-12 rounded-lg border border-border/50"
              style={{
                background: designSettings.gradient.type === 'linear'
                  ? `linear-gradient(${designSettings.gradient.rotation}deg, ${designSettings.gradient.color1}, ${designSettings.gradient.color2})`
                  : `radial-gradient(circle, ${designSettings.gradient.color1}, ${designSettings.gradient.color2})`
              }}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
}
